import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Binary consent state that gates first-party GA4 analytics on the main site. */
export type AppConsentState = 'granted' | 'denied';

/**
 * First-party consent signal for the main SSR application.
 *
 * On the main TTPOI site there is no separate cookie banner — the consent
 * gate is the **Privacy Notice ("Notice") acceptance** in the agreement pop-up.
 * When the user accepts the Privacy Notice, `grant()` is called (see
 * RegistrationCoreComponent), which flips this state and releases any GA4
 * page_view that AppTrackingService buffered while consent was pending.
 *
 * Default is `denied` — no GA4 event fires until the Privacy Notice is accepted.
 * The decision is persisted in sessionStorage so a reload within the same
 * browser session does not re-gate tracking.
 *
 * This is distinct from the embed `ConsentService` (attribute-driven, provided
 * only in the web-component bundle). This service is root-provided for the SSR app.
 */
@Injectable({ providedIn: 'root' })
export class AppConsentService {
  private static readonly STORAGE_KEY = 'ttpoi_app_consent';

  private readonly platformId = inject(PLATFORM_ID);
  private readonly _state$ = new BehaviorSubject<AppConsentState>(this.readInitial());

  /** Observable stream of consent state changes. */
  readonly state$: Observable<AppConsentState> = this._state$.asObservable();

  /** Synchronous read of the current consent state. */
  get state(): AppConsentState {
    return this._state$.getValue();
  }

  /** Grant consent (called when the Privacy Notice is accepted). */
  grant(): void {
    this.set('granted');
  }

  /** Revoke consent. */
  revoke(): void {
    this.set('denied');
  }

  private set(next: AppConsentState): void {
    if (this._state$.getValue() === next) return;
    this._state$.next(next);
    this.persist(next);
  }

  private readInitial(): AppConsentState {
    if (!isPlatformBrowser(this.platformId)) return 'denied';
    try {
      return sessionStorage.getItem(AppConsentService.STORAGE_KEY) === 'granted'
        ? 'granted'
        : 'denied';
    } catch {
      return 'denied';
    }
  }

  private persist(state: AppConsentState): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      sessionStorage.setItem(AppConsentService.STORAGE_KEY, state);
    } catch {
      /* storage unavailable (private mode / quota) — in-memory state still applies */
    }
  }
}
